/**
 * Sessions API Service
 *
 * This service provides methods for fetching session history and user statistics.
 * It handles data fetching for the dashboard page, including session history
 * and aggregate statistics.
 *
 * The service is designed to easily switch from mock data to real API calls.
 * Set the REACT_APP_API_BASE_URL environment variable to connect to the real API.
 *
 * @module services/sessions
 */

import { Session, UserStats } from '../types/session';
import { getIdToken } from './auth';

/**
 * API configuration from environment variables.
 * Falls back to empty string for mock mode when not configured.
 */
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Flag to determine if we should use mock data.
 * When API_BASE_URL is not set, the service operates in mock mode.
 */
const USE_MOCK_DATA = !API_BASE_URL;

/**
 * Error codes for sessions-related errors.
 */
export type SessionsErrorCode =
  | 'FETCH_FAILED'
  | 'UNAUTHORIZED'
  | 'NOT_FOUND'
  | 'NETWORK_ERROR'
  | 'UNKNOWN';

/**
 * Custom error class for sessions-related errors.
 * Provides structured error information for better error handling.
 */
export class SessionsError extends Error {
  /** HTTP status code if applicable */
  statusCode: number;
  /** Error code for programmatic error handling */
  code: SessionsErrorCode;
  /** Original error if this error wraps another */
  originalError?: Error;

  constructor(
    message: string,
    statusCode: number,
    code: SessionsErrorCode = 'UNKNOWN',
    originalError?: Error
  ) {
    super(message);
    this.name = 'SessionsError';
    this.statusCode = statusCode;
    this.code = code;
    this.originalError = originalError;
  }
}

/**
 * Mock session data for development and testing.
 * This data is used when the API is not configured.
 */
const MOCK_SESSIONS: Session[] = [
  {
    id: 'session-1',
    date: '2024-01-15T10:30:00Z',
    scenarioId: 'scenario-1',
    scenarioName: 'Enterprise Software Sales Call',
    role: 'Sales Representative',
    score: 85,
    status: 'completed',
    transcriptUrl: '/transcripts/session-1.txt',
    evaluationUrl: '/evaluations/session-1.pdf',
  },
  {
    id: 'session-2',
    date: '2024-01-14T14:00:00Z',
    scenarioId: 'scenario-2',
    scenarioName: 'Patient Consultation',
    role: 'Healthcare Provider',
    score: 92,
    status: 'completed',
    transcriptUrl: '/transcripts/session-2.txt',
    evaluationUrl: '/evaluations/session-2.pdf',
  },
  {
    id: 'session-3',
    date: '2024-01-13T09:15:00Z',
    scenarioId: 'scenario-3',
    scenarioName: 'Investment Advisory Meeting',
    role: 'Financial Advisor',
    score: 78,
    status: 'completed',
    transcriptUrl: '/transcripts/session-3.txt',
    evaluationUrl: '/evaluations/session-3.pdf',
  },
  {
    id: 'session-4',
    date: '2024-01-12T16:45:00Z',
    scenarioId: 'scenario-4',
    scenarioName: 'Customer Support Escalation',
    role: 'Support Specialist',
    score: 88,
    status: 'completed',
    transcriptUrl: '/transcripts/session-4.txt',
    evaluationUrl: '/evaluations/session-4.pdf',
  },
  {
    id: 'session-5',
    date: '2024-01-11T11:00:00Z',
    scenarioId: 'scenario-1',
    scenarioName: 'Enterprise Software Sales Call',
    role: 'Sales Representative',
    score: null,
    status: 'cancelled',
  },
  {
    id: 'session-6',
    date: '2024-01-10T13:30:00Z',
    scenarioId: 'scenario-5',
    scenarioName: 'Insurance Claim Discussion',
    role: 'Claims Adjuster',
    score: 81,
    status: 'completed',
    transcriptUrl: '/transcripts/session-6.txt',
    evaluationUrl: '/evaluations/session-6.pdf',
  },
];

/**
 * Helper function to make authenticated API requests.
 *
 * @param endpoint - The API endpoint (relative to API_BASE_URL)
 * @param options - Optional fetch options
 * @returns Promise resolving to the parsed JSON response
 * @throws SessionsError if the request fails
 */
async function fetchWithAuth<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const token = await getIdToken();

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    };

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorMessage = await response.text().catch(() => 'Unknown error');
      const code = getErrorCode(response.status);
      throw new SessionsError(
        errorMessage || `Request failed with status ${response.status}`,
        response.status,
        code
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof SessionsError) {
      throw error;
    }
    // Handle network errors
    throw new SessionsError(
      'Network error occurred while fetching data',
      0,
      'NETWORK_ERROR',
      error instanceof Error ? error : undefined
    );
  }
}

/**
 * Map HTTP status codes to error codes.
 *
 * @param statusCode - HTTP status code
 * @returns Corresponding SessionsErrorCode
 */
function getErrorCode(statusCode: number): SessionsErrorCode {
  switch (statusCode) {
    case 401:
    case 403:
      return 'UNAUTHORIZED';
    case 404:
      return 'NOT_FOUND';
    default:
      return 'FETCH_FAILED';
  }
}

/**
 * Simulate network delay for mock data.
 * This helps test loading states in the UI.
 *
 * @param ms - Delay in milliseconds
 */
function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Calculate user statistics from session data.
 *
 * @param sessions - Array of session objects
 * @returns UserStats object with calculated statistics
 */
function calculateStats(sessions: Session[]): UserStats {
  const completedSessions = sessions.filter(
    (session) => session.status === 'completed' && session.score !== null
  );

  const totalScore = completedSessions.reduce(
    (sum, session) => sum + (session.score || 0),
    0
  );

  const averageScore =
    completedSessions.length > 0
      ? Math.round(totalScore / completedSessions.length)
      : 0;

  return {
    averageScore,
    sessionsCompleted: completedSessions.length,
    totalSessions: sessions.length,
  };
}

/**
 * Fetch session history for the current user.
 *
 * Returns a list of all training sessions for the authenticated user,
 * sorted by date (most recent first). In mock mode, returns predefined
 * mock data after a simulated delay.
 *
 * @returns Promise resolving to an array of Session objects
 * @throws SessionsError if the request fails (in real API mode)
 *
 * @example
 * ```typescript
 * try {
 *   const sessions = await getSessionHistory();
 *   console.log('Session count:', sessions.length);
 * } catch (error) {
 *   if (error instanceof SessionsError) {
 *     console.error('Failed to fetch sessions:', error.message);
 *   }
 * }
 * ```
 */
export async function getSessionHistory(): Promise<Session[]> {
  // Always use real API when configured
  if (API_BASE_URL) {
    try {
      const sessions = await fetchWithAuth<Session[]>('/sessions');
      // Sort by date (most recent first)
      return sessions.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    } catch (error) {
      console.error('Failed to fetch sessions from API:', error);
      // Return empty array on error instead of throwing
      return [];
    }
  }

  // Fallback to mock data only if API not configured
  await delay(500);
  return [...MOCK_SESSIONS].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
  );
}

/**
 * Fetch user statistics for the dashboard.
 *
 * Returns aggregate statistics including average score, sessions completed,
 * and total sessions. In mock mode, calculates statistics from mock session
 * data after a simulated delay.
 *
 * @returns Promise resolving to a UserStats object
 * @throws SessionsError if the request fails (in real API mode)
 *
 * @example
 * ```typescript
 * try {
 *   const stats = await getUserStats();
 *   console.log('Average score:', stats.averageScore);
 *   console.log('Sessions completed:', stats.sessionsCompleted);
 * } catch (error) {
 *   if (error instanceof SessionsError) {
 *     console.error('Failed to fetch stats:', error.message);
 *   }
 * }
 * ```
 */
export async function getUserStats(): Promise<UserStats> {
  if (USE_MOCK_DATA) {
    // Simulate network delay for realistic loading state testing
    await delay(300);
    return calculateStats(MOCK_SESSIONS);
  }

  return fetchWithAuth<UserStats>('/users/stats');
}

/**
 * Sessions service object for convenient access to all session-related functions.
 * Provides a unified interface for session history and statistics operations.
 */
export const sessionsService = {
  getSessionHistory,
  getUserStats,
};

export default sessionsService;
